import { toast } from "sonner";

interface ImportMetaEnv {
  VITE_API_URL?: string;
  [key: string]: string | undefined;
}

interface ApiRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  skipAuth?: boolean;
  skipContentType?: boolean;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  ok: boolean;
  status?: number;
}

interface ErrorResponse {
  message?: string;
}

let isLoggingOutDueToBlock = false;
let isLoggingOutDueToExpiry = false;

// CSRF + refresh helpers ------------------------------------------------------
const CSRF_HEADER = "X-CSRF-Token";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Cookies sao per-domain. Front (caas-dashboard-*) e back (caas-super-app-*)
// ficam em sub-dominios diferentes, entao o JS daqui NUNCA consegue ler o
// cookie liqicaas.csrf setado pelo backend (mesmo nao sendo HttpOnly).
// Solucao: backend retorna csrfToken no body de /auth/login e /auth/refresh.
// Guardamos em memoria (module-scoped) e ecoamos em header X-CSRF-Token.
// Backend continua validando header === cookie (ele ve o cookie cross-site OK).
let csrfTokenMemory: string | null = null;

/** Salva o csrf token recebido do backend (login, refresh, sso). */
export function setCsrfToken(token: string | null | undefined): void {
  csrfTokenMemory = token ?? null;
}

export function getCsrfToken(): string | null {
  if (csrfTokenMemory) return csrfTokenMemory;
  // Fallback: hard reload zerou a memoria mas o cookie `liqicaas.csrf` continua
  // setado (não-HttpOnly por design double-submit). Ler do document.cookie
  // evita CSRF 403 em mutations feitas logo após reload (ex: page.goto em E2E
  // ou F5 do usuário). O backend continua validando header === cookie.
  if (typeof document !== "undefined") {
    const match = document.cookie.match(/(?:^|;\s*)liqicaas\.csrf=([^;]+)/);
    if (match?.[1]) {
      csrfTokenMemory = decodeURIComponent(match[1]);
      return csrfTokenMemory;
    }
  }
  return null;
}
// Endpoints pre-auth ou que NÃO devem disparar refresh em 401 (evita loop).
// Atenção: /auth/me também conta — quando o usuário está deslogado em /login,
// initialize() chama /auth/me que retorna 401 legítimo; tentar refresh aqui
// dispararia outro 401, redirect /login, novo initialize, loop infinito.
// Convenção: QUALQUER endpoint sob /auth/ ou /sso/ é pre-auth/auth-related
// e não deve disparar refresh nem redirect automático em 401.
function isAuthRelatedEndpoint(endpoint: string): boolean {
  return endpoint.startsWith("/auth/") || endpoint.startsWith("/sso/");
}

// Dedupe concurrent refresh attempts — múltiplas requisições simultâneas com
// access token expirado devem aguardar UMA chamada a /auth/refresh.
let pendingRefresh: Promise<boolean> | null = null;
async function attemptRefresh(baseUrl: string): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      // /auth/refresh é @Public no backend (CSRF skip via lista pre-auth),
      // mas mandamos o header se já tivermos token (defesa em profundidade).
      const csrf = csrfTokenMemory;
      const res = await fetch(`${baseUrl}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: csrf
          ? { "Content-Type": "application/json", [CSRF_HEADER]: csrf }
          : { "Content-Type": "application/json" },
      });
      if (res.ok) {
        // Atualiza csrf token em memória com o novo emitido pelo backend
        try {
          const body = await res.json();
          if (body?.csrfToken) setCsrfToken(body.csrfToken);
        } catch {
          /* ignore parse errors — refresh OK é o que importa */
        }
      }
      return res.ok;
    } catch {
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();
  return pendingRefresh;
}

/** Translate common English API error messages to PT-BR */
const ERROR_TRANSLATIONS: Record<string, string> = {
  "Invalid credentials": "Credenciais inválidas",
  "Unauthorized": "Não autorizado",
  "Forbidden": "Acesso negado",
  "Not found": "Não encontrado",
  "Internal server error": "Erro interno do servidor",
  "Bad request": "Requisição inválida",
  "Conflict": "Conflito — registro já existe",
  "Too many requests": "Muitas requisições. Aguarde um momento.",
  "Service unavailable": "Serviço temporariamente indisponível",
};

function translateError(message: string): string {
  // Exact match
  if (ERROR_TRANSLATIONS[message]) return ERROR_TRANSLATIONS[message];
  // Case-insensitive match
  const lower = message.toLowerCase();
  for (const [en, pt] of Object.entries(ERROR_TRANSLATIONS)) {
    if (lower === en.toLowerCase()) return pt;
  }
  return message;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    const env = import.meta.env as ImportMetaEnv;
    const rawUrl = env.VITE_API_URL || "http://localhost:3000";

    let baseUrl = rawUrl.trim();
    if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
      baseUrl = `https://${baseUrl}`;
    }

    baseUrl = baseUrl.replace(/\/$/, "");

    this.baseUrl = baseUrl;
  }

  async request<T = unknown>(
    endpoint: string,
    options: ApiRequestOptions = {},
    _retryAfterRefresh = false,
  ): Promise<ApiResponse<T>> {
    const { method = "GET", headers = {}, body, skipContentType = false } = options;

    try {
      const url = `${this.baseUrl}${endpoint}`;

      // Auto-injetar CSRF token em mutations (skip se já veio explicitamente)
      const finalHeaders: Record<string, string> = {
        ...(skipContentType ? {} : { "Content-Type": "application/json" }),
        ...headers,
      };
      if (UNSAFE_METHODS.has(method.toUpperCase()) && !finalHeaders[CSRF_HEADER]) {
        // Usa getCsrfToken() em vez de ler memory direto — getter agora tem
        // fallback pra ler do cookie liqicaas.csrf quando memory tá vazia
        // (acontece após hard reload antes do próximo /auth/refresh).
        const csrf = getCsrfToken();
        if (csrf) finalHeaders[CSRF_HEADER] = csrf;
      }

      const config: Record<string, unknown> = {
        method,
        cache: "no-store",
        credentials: "include",
        headers: finalHeaders,
      };

      if (body && method !== "GET") {
        config.body = skipContentType ? body : JSON.stringify(body);
      }

      let response: Response;
      try {
        response = await fetch(url, config as RequestInit);
      } catch (fetchError) {
        const isNetworkError =
          fetchError instanceof TypeError &&
          (fetchError.message.includes("Failed to fetch") ||
            fetchError.message.includes("NetworkError") ||
            fetchError.message.includes("Network request failed"));

        if (isNetworkError) {
          return {
            ok: false,
            error: "Erro de conexão. Verifique sua internet e tente novamente.",
            status: 0,
          };
        }

        throw fetchError;
      }

      let data: T | undefined;
      const contentType = response.headers.get("content-type");
      const isJsonContentType = contentType && contentType.includes("application/json");

      if (isJsonContentType || response.ok) {
        try {
          const responseClone = response.clone();
          try {
            data = await response.json();
          } catch {
            const text = await responseClone.text();
            if (text && text.trim()) {
              if (text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html")) {
                if (!response.ok) {
                  const errorMatch = text.match(/<title[^>]*>([^<]+)<\/title>/i) || text.match(/<h1[^>]*>([^<]+)<\/h1>/i);
                  const errorMessage = errorMatch ? errorMatch[1] : `Request failed with status ${response.status}`;
                  return {
                    ok: false,
                    error: errorMessage,
                    status: response.status,
                  };
                }
              } else {
                try {
                  data = JSON.parse(text) as T;
                } catch {
                  // could not parse response
                }
              }
            }
          }
        } catch {
          // error reading response
        }
      }

      if (!response.ok) {
        const errorData = data as ErrorResponse | undefined;
        const rawMessage = errorData?.message;
        const messageStr = Array.isArray(rawMessage) ? rawMessage.join(". ") : (rawMessage || `Request failed with status ${response.status}`);
        const errorMessage = translateError(String(messageStr));

        if (response.status === 401 && !isLoggingOutDueToExpiry) {
          const isAuthEndpoint = isAuthRelatedEndpoint(endpoint);
          if (!isAuthEndpoint && !_retryAfterRefresh) {
            const refreshed = await attemptRefresh(this.baseUrl);
            if (refreshed) {
              return this.request<T>(endpoint, options, true);
            }
          }
          // Só redireciona se NÃO está em endpoint /auth/* e NÃO está já em /login.
          // Evita loop quando initialize() chama /auth/me no /login e recebe 401 legítimo.
          if (!isAuthEndpoint && typeof window !== "undefined" && window.location?.pathname !== "/login") {
            isLoggingOutDueToExpiry = true;
            window.location.href = "/login";
            setTimeout(() => {
              isLoggingOutDueToExpiry = false;
            }, 5000);
          }
        }

        if (response.status === 403 && errorMessage.includes("bloqueada") && !isLoggingOutDueToBlock) {
          isLoggingOutDueToBlock = true;
          toast.error("Sua conta foi bloqueada. Você será desconectado.");
          window.location.href = "/login";
          setTimeout(() => {
            isLoggingOutDueToBlock = false;
          }, 5000);
        }

        return {
          ok: false,
          error: errorMessage,
          data: data,
          status: response.status,
        };
      }

      return {
        ok: true,
        data: data as T,
        status: response.status,
      };
    } catch (error) {
      if (error instanceof TypeError) {
        if (
          error.message.includes("Failed to fetch") ||
          error.message.includes("NetworkError") ||
          error.message.includes("Network request failed")
        ) {
          return {
            ok: false,
            error: "Erro de conexão. Verifique sua internet e tente novamente.",
            status: 0,
          };
        }
      }

      return {
        ok: false,
        error: error instanceof Error ? error.message : "Erro de conexão com o servidor",
        status: 0,
      };
    }
  }

  async get<T = unknown>(endpoint: string, options?: Omit<ApiRequestOptions, "method">): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  async post<T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "POST", body });
  }

  async put<T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "PUT", body });
  }

  async patch<T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "PATCH", body });
  }

  async delete<T = unknown>(endpoint: string, options?: Omit<ApiRequestOptions, "method">): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  async uploadWithFiles<T = unknown>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: formData,
      skipContentType: true,
    });
  }

  async putFormData<T = unknown>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: formData,
      skipContentType: true,
    });
  }
}

export const apiService = new ApiService();
export default apiService;
